/**
 * Generated by scripts/gen_variable_metadata.py. Do not edit manually.
 * Source: server/variables/builtin_variable_registry.py.
 */
import type { ResolutionStage } from 'api/generated/types';

export const NAMESPACE_PREFIXES = [
  'job',
  'candidate',
  'application',
  'call',
  'followup',
  'script',
] as const;

export type NamespacePrefix = (typeof NAMESPACE_PREFIXES)[number];

export const NAMESPACE_FIELDS: Readonly<
  Record<Exclude<NamespacePrefix, 'job'>, ReadonlySet<string>>
> = {
  candidate: new Set([
    'full_name',
    'first_name',
    'email',
    'phone_number',
    'zip_code',
    'linkedin_profile_url',
    'date_available',
  ]),
  application: new Set(['stage']),
  call: new Set([
    'length_sec',
    'from_phone_number',
    'recruiter_phone_number',
    'interview_link',
    'estimated_duration_minutes',
    'expiration_days',
  ]),
  followup: new Set(['interviewer_name', 'interviewer_email']),
  script: new Set(['book_call', 'custom_outro']),
};

export const NAMESPACE_RESOLUTION_STAGE: Readonly<
  Record<NamespacePrefix, ResolutionStage>
> = {
  job: 'config',
  candidate: 'send',
  application: 'send',
  call: 'call',
  followup: 'send',
  script: 'send',
};

export const CALL_FIELD_RESOLUTION_STAGES: Readonly<
  Record<string, ResolutionStage>
> = {
  estimated_duration_minutes: 'config',
  expiration_days: 'config',
  from_phone_number: 'call',
  recruiter_phone_number: 'config',
  interview_link: 'send',
  length_sec: 'call',
};

export const EDITOR_FIELD_SCOPED_NAMESPACED_VARIABLES: ReadonlySet<string> =
  new Set([
    'call.interview_link',
    'followup.interviewer_name',
    'followup.interviewer_email',
    'script.book_call',
    'script.custom_outro',
  ]);

export const EDITOR_FIELD_SCOPED_LEGACY_VARIABLES: ReadonlySet<string> =
  new Set([
    'interview_link',
    'interviewer_name',
    'interviewer_email',
    'book_call',
    'custom_outro',
  ]);

export const EDITOR_SYSTEM_VARIABLE_NAMES: ReadonlySet<string> = new Set([
  'candidate_name',
  'interview_link',
  'ai_name',
  'AI_name',
  'recruiter_phone_number',
  'book_call',
  'expiration_days',
  'interviewer_name',
  'interviewer_email',
  'job_name',
  'company_name',
  'candidate_phone_number',
  'custom_outro',
  'estimated_duration_minutes',
  '_legacy_company_name',
]);

export interface VariableLookupPolicy {
  readonly lookupKeys: readonly string[];
  readonly emptyOverrideKeys?: readonly string[];
  readonly emptyShadowKey?: string;
  readonly shadowedLookupKeys?: readonly string[];
}

export const BUILTIN_VARIABLE_LOOKUP_POLICIES: Readonly<
  Record<string, VariableLookupPolicy>
> = {
  ai_name: {
    lookupKeys: ['ai_name', 'job.ai_name'],
    shadowedLookupKeys: ['job.ai_name'],
    emptyShadowKey: 'ai_name',
  },
  AI_name: {
    lookupKeys: ['ai_name', 'job.ai_name', 'AI_name'],
    shadowedLookupKeys: ['job.ai_name'],
    emptyShadowKey: 'ai_name',
  },
  'job.ai_name': {
    lookupKeys: ['ai_name', 'job.ai_name', 'AI_name'],
    shadowedLookupKeys: ['job.ai_name', 'AI_name'],
    emptyShadowKey: 'ai_name',
  },
  'job.external_name': {
    lookupKeys: ['job_name', 'job.external_name'],
  },
  'job.internal_name': {
    lookupKeys: ['job.internal_name', 'name', 'job.name'],
  },
  company_name: {
    lookupKeys: [
      'company_name',
      'job.hiring_company_name',
      '_legacy_company_name',
      'job.company_name',
    ],
    emptyOverrideKeys: ['company_name', 'job.hiring_company_name'],
  },
  'job.hiring_company_name': {
    lookupKeys: ['job.hiring_company_name', 'company_name'],
  },
  'job.company_name': {
    lookupKeys: ['job.company_name', 'company_name', '_legacy_company_name'],
  },
  candidate_name: {
    lookupKeys: ['candidate_name', 'candidate.first_name'],
  },
  'script.book_call': {
    lookupKeys: ['script.book_call', 'book_call'],
  },
  book_call: {
    lookupKeys: ['book_call', 'script.book_call'],
  },
  'script.custom_outro': {
    lookupKeys: ['script.custom_outro', 'custom_outro'],
  },
  custom_outro: {
    lookupKeys: ['custom_outro', 'script.custom_outro'],
  },
  'call.interview_link': {
    lookupKeys: ['interview_link', 'call.interview_link'],
  },
  interview_link: {
    lookupKeys: ['interview_link', 'call.interview_link'],
  },
  'call.estimated_duration_minutes': {
    lookupKeys: [
      'call.estimated_duration_minutes',
      'estimated_duration_minutes',
    ],
  },
  estimated_duration_minutes: {
    lookupKeys: [
      'estimated_duration_minutes',
      'call.estimated_duration_minutes',
    ],
  },
  'call.expiration_days': {
    lookupKeys: ['call.expiration_days', 'expiration_days'],
  },
  expiration_days: {
    lookupKeys: ['expiration_days', 'call.expiration_days'],
  },
  'call.recruiter_phone_number': {
    lookupKeys: ['recruiter_phone_number', 'call.recruiter_phone_number'],
  },
  recruiter_phone_number: {
    lookupKeys: ['recruiter_phone_number', 'call.recruiter_phone_number'],
  },
  'followup.interviewer_name': {
    lookupKeys: ['followup.interviewer_name', 'interviewer_name'],
  },
  interviewer_name: {
    lookupKeys: ['interviewer_name', 'followup.interviewer_name'],
  },
  'followup.interviewer_email': {
    lookupKeys: ['followup.interviewer_email', 'interviewer_email'],
  },
  interviewer_email: {
    lookupKeys: ['interviewer_email', 'followup.interviewer_email'],
  },
  'job.recruiter_email': {
    lookupKeys: ['job.recruiter_email', 'recruiter_email', 'results_email'],
  },
  recruiter_email: {
    lookupKeys: ['recruiter_email', 'job.recruiter_email', 'results_email'],
  },
  results_email: {
    lookupKeys: ['results_email', 'job.recruiter_email', 'recruiter_email'],
  },
  'candidate.phone_number': {
    lookupKeys: [
      'candidate.phone_number',
      'candidate_phone_number',
      'candidate_phone',
    ],
  },
  candidate_phone_number: {
    lookupKeys: [
      'candidate_phone_number',
      'candidate.phone_number',
      'candidate_phone',
    ],
  },
  candidate_phone: {
    lookupKeys: [
      'candidate_phone',
      'candidate.phone_number',
      'candidate_phone_number',
    ],
  },
};

// Aliases without their own spelling-specific policy use the canonical policy.
export const BUILTIN_LOOKUP_ALIASES: Readonly<Record<string, string>> = {
  position_title: 'job.external_name',
  position_name: 'job.external_name',
  posting_title: 'job.external_name',
  job_name: 'job.external_name',
  'job.name': 'job.internal_name',
};
